import React, { useEffect, useCallback } from 'react';
import UniversalQuoterComponent from '../components/universal_quoter/UniversalQuoterComponent';
import { createRoot } from 'react-dom/client';
import ApiUtils from 'utils/AnnuitiesApi';
import { STATES } from 'constants/App';
import AlqApi from '../utils/AlqApi';
import UniversalApi from '../utils/UniversalApi';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { annuityProductTypeMap } from '../constants/AnnuitiesSolveTypes';
import { QUOTER_TABS, ALLOWED_QUOTER_TABS } from '../constants/QuoterTabs';
import ProposedInsured from '../components/alq/nmb_term_eticket/ProposedInsured';

const config = require('../config/App');

posthog.init(config.PostHogKey, {
  api_host: 'https://us.i.posthog.com'
});

window.IXN = {};
const IXN_AUTH = Cookies.get('ixn') ? Cookies.getJSON('ixn') : null;
//const COOKIED_CRITERIA = Cookies.getJSON('hexure_annuities');
const QUOTER_CONFIG = window.HEXURE_UQ_CONFIG;
// Normalize and validate startTab from config. Accept only 'new-annuity' or 'new-life'.
const _rawStartTab = QUOTER_CONFIG && QUOTER_CONFIG.startTab ? String(QUOTER_CONFIG.startTab).toLowerCase() : null;
const _allowedTabs = new Set(ALLOWED_QUOTER_TABS);
const defaultStartTab = _allowedTabs.has(_rawStartTab) ? _rawStartTab : null;

const UniversalQuoter = () => {
  const [aaqProductfeatures, setAAQProductFeatures] = React.useState([]);
  const [aaqCriteriaOptions, setAAQCriteriaOptions] = React.useState({
    default_state: 'AL',
    states: STATES,
    product_types: [],
    required_agent_info: [],
    features: aaqProductfeatures?.map(c => c.id) || []
  });
  const [aaqQuoter, setAAQQuoter] = React.useState({});
  const [alqQuoter, setALQQuoter] = React.useState({});
  const [alqGuides, setALQGuides] = React.useState([]);
  const [ltcQuoter, setLTCQuoter] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [aaq, setAAQ] = React.useState({});
  // Determine if the UQ default life selection is set in any legacy/global place
  const isUQDefaultLifeSelected = !!(
    window?.ixn_data?.IXNModel?.IsUQDefaultLifeSelected ||
    window?.EditQuoteModel?.IXNQuote?.IXNModel?.IsUQDefaultLifeSelected ||
    window?.ixnDataModel?.IXNModel?.IsUQDefaultLifeSelected
  );

  // Priority: if UQ default life is selected, honor it (-> 'new-life').
  // Otherwise, if a valid startTab is provided in QUOTER_CONFIG, use that.
  // Fallback to 'new-annuity'.
  const defaultTab = isUQDefaultLifeSelected ? 'new-life' : defaultStartTab ? defaultStartTab : 'new-annuity';
  const [activeTab, setActiveTab] = React.useState(defaultTab);

  // Store carriers and product features for each quoter type
  const [aaqCarriers, setAAQCarriers] = React.useState([]);
  const [aaqProducts, setAAQProducts] = React.useState([]);
  const [aaqProductsFeatures, setAAQProductsFeatures] = React.useState([]);
  const [solveType, setSolveType] = React.useState(null);
  const [alqCarriers, setALQCarriers] = React.useState([]);
  const [alqProductFeatures, setALQProductFeatures] = React.useState([]);
  // Store Universal Quoter data
  const [uqData, setUqData] = React.useState(null);
  const [isUniversalQuoterLoaded, setIsUniversalQuoterLoaded] = React.useState(false);

  // Store current form data from UniversalQuoterComponent
  const [currentFormData, setCurrentFormData] = React.useState({});

  // Store tab-specific form data to preserve state when switching tabs (for unsaved quotes)
  const [annuityTabFormData, setAnnuityTabFormData] = React.useState(null);
  const [lifeTabFormData, setLifeTabFormData] = React.useState(null);

  // Store life product options
  const [lifeProductOptions, setLifeProductOptions] = React.useState({
    term_products: [],
    guaranteed_life_products: [],
    iul_products: [],
    return_of_premium_products: [],
    other_products: [],
    product_limits: 3
  });

  // Track which APIs have been loaded to avoid duplicate calls
  const [loadedTabs, setLoadedTabs] = React.useState(new Set());

  // Function to load Universal Quoter data first
  const loadUniversalQuoter = () => {
    setLoading(true);
    setError(null);

    return UniversalApi.loadUniversalQuoter()
      .then(({ data }) => {
        const { is_deactivated, default_lob, universal_quoter_details = [] } = data;

        const annuityQuoter = universal_quoter_details.find(detail => detail?.quoter_type === 1);
        const lifeQuoter = universal_quoter_details.find(detail => detail?.quoter_type === 2);

        const isAnnuityDeactivated = Boolean(annuityQuoter?.is_deactivated);
        const isLifeDeactivated = Boolean(lifeQuoter?.is_deactivated);

        let nextTab = activeTab;

        if (isAnnuityDeactivated && !isLifeDeactivated) {
          nextTab = 'new-life';
        } else if (!isAnnuityDeactivated && isLifeDeactivated) {
          nextTab = 'new-annuity';
        } else if (!isAnnuityDeactivated && !isLifeDeactivated) {
          // Determine default tab based on default Line Of Business
          if (QUOTER_CONFIG?.embed_context === 'fire_light') {
            if (isUQDefaultLifeSelected) {
              nextTab = 'new-life';
            } else {
              nextTab = 'new-annuity';
            }
          } else if (default_lob === 1) {
            nextTab = 'new-annuity';
          } else if (default_lob === 2) {
            nextTab = 'new-life';
          }
        }

        setActiveTab(nextTab);
        setUqData(data);
        // we consider Universal Quoter as not loaded to show appropriate messaging in the UI
        if (isAnnuityDeactivated && isLifeDeactivated) {
          setIsUniversalQuoterLoaded(false); // If both quoters are deactivated Annuity and Life
        } else {
          setIsUniversalQuoterLoaded(!is_deactivated); // universal quoter is deactivated status
        }
        return data;
      })
      .catch(error => {
        setLoading(false);
        setIsUniversalQuoterLoaded(false);
        if (error.response && error.response.data) {
          setError(error.response.data[0]);
        } else {
          setError({ type: -1, message: 'Failed to load Universal Quoter configuration' });
        }
        throw error;
      });
  };

  // Function to load data based on active tab (only called after Universal Quoter is loaded)
  const loadQuoterData = (tab, uqDataOverride = null) => {
    const dataSource = uqDataOverride || uqData;

    // Ensure Universal Quoter is loaded first
    if (!isUniversalQuoterLoaded && !uqDataOverride) {
      return;
    }

    // Check if this tab's data has already been loaded
    if (loadedTabs.has(tab)) {
      return;
    }
    setLoading(true);
    setError(null);

    if (tab === 'new-annuity') {
      // Load Annuity Quoter - find by quoter_type = 1
      const annuityQuoter = dataSource?.universal_quoter_details?.find(detail => detail.quoter_type === 1);

      if (!annuityQuoter?.quoter_token_id) {
        setLoading(false);
        setError({ type: -1, message: 'Annuity quoter token ID not found in Universal Quoter data' });
        return;
      }

      ApiUtils.loadQuoter({ universal: true, id: annuityQuoter.quoter_token_id })
        .then(({ data }) => {
          // Map full aaq product type names to abbreviations
          const aaq_product_types_abbreviations = {
            'Fixed Indexed Annuity': 'FIA',
            'Fixed Annuity': 'FA',
            'Multi-Year Guaranteed Annuity': 'MYGA'
          };
          const aaq_product_types_desired_order = ['FA', 'MYGA', 'FIA']; // Desired order of aaq product types
          // Simplify product types and sort them
          const simplified_aaq_product_types =
            data?.product_types?.map(item => aaq_product_types_abbreviations[item]).sort((a, b) => aaq_product_types_desired_order?.indexOf(a) - aaq_product_types_desired_order?.indexOf(b)) ||
            aaq_product_types_desired_order;
          setAAQCriteriaOptions({ ...aaqCriteriaOptions, required_agent_info: data.required_agent_info, product_types: simplified_aaq_product_types });
          setAAQQuoter(data);
          setAAQ(data);

          if (data.solve_type !== null && data.solve_type !== undefined) {
            setSolveType(data.solve_type);
          }

          // Add annuity token to window config
          if (!window.HEXURE_UQ_CONFIG) {
            window.HEXURE_UQ_CONFIG = {};
          }
          window.HEXURE_UQ_CONFIG.annuity_token = annuityQuoter.quoter_token_id;

          // Load carriers and product features for annuity quoter
          const batchSize = 200;
          const productPromises = [];
          const productFeatures = ['Death Benefits', 'Income Benefits', 'Living Benefits', 'Market Value Adjustment', 'Online Application'];

          // Load products in batches if product_ids exist
          if (data.product_ids && data.product_ids.length > 0) {
            for (let i = 0; i < data.product_ids.length; i += batchSize) {
              const batchIds = data.product_ids.slice(i, i + batchSize);
              productPromises.push(ApiUtils.loadProduct({ id: batchIds }));
            }
          }

          // Load carriers and product features
          Promise.all([ApiUtils.loadCarriers({ id: data.carrier_ids }), ApiUtils.loadProductFeatures({ universal: true, id: annuityQuoter.quoter_token_id }), ...productPromises])
            .then(responses => {
              const carriers = responses.shift();
              const product_features = responses?.shift()?.data?.filter(feature => productFeatures?.includes(feature.name));
              const products = responses
                ?.flatMap(innerArray => innerArray)
                ?.map(product => {
                  product.features = product?.product_feature_ids?.map(id => {
                    return product_features?.find(pf => pf.id === id);
                  });
                  return product;
                });

              setAAQCarriers(carriers);
              setAAQProducts(products);
              setAAQProductsFeatures(product_features);
              setAAQCriteriaOptions(prev => ({
                ...prev,
                features: product_features?.map(c => c.id) || []
              }));
              setLoading(false);

              // Mark this tab as loaded
              setLoadedTabs(prev => new Set([...prev, tab]));
            })
            .catch(error => {
              console.error('Error loading annuity carriers/products:', error);
              setLoading(false);
              // Mark this tab as loaded even if carriers/products fail to prevent infinite retries
              setLoadedTabs(prev => new Set([...prev, tab]));
            });
        })
        .catch(error => {
          setLoading(false);
          console.error('Error loading annuity quoter:', error);
          if (error.response && error.response.data) {
            setError(error.response.data[0]);
          } else {
            setError({ type: -1, message: 'Please Provide the BGA-Quoter Token' });
          }
        });
    } else if (tab === 'new-life') {
      // Load Life Quoter - find by quoter_type = 2
      const lifeQuoter = dataSource?.universal_quoter_details?.find(detail => detail.quoter_type === 2);

      if (!lifeQuoter?.quoter_token_id) {
        setLoading(false);
        setError({ type: -1, message: 'Life quoter token ID not found in Universal Quoter data' });
        return;
      }

      AlqApi.loadBgaQuoter({ universal: true, id: lifeQuoter.quoter_token_id })
        .then(({ data }) => {
          setALQQuoter(data);

          // Add life token to window config
          if (!window.HEXURE_UQ_CONFIG) {
            window.HEXURE_UQ_CONFIG = {};
          }
          window.HEXURE_UQ_CONFIG.life_token = lifeQuoter.quoter_token_id;
          try {
            // Read current ixn cookie (if any), set the health analyzer token, and persist it back
            const currentIxn = Cookies.getJSON('ixn') || {};
            currentIxn.ha_api_token = data.health_analyzer_token;
            Cookies.set('ixn', currentIxn, { domain: window.location.hostname });
          } catch (e) {
            // fail gracefully and log for debugging
            console.error('Failed to set ha_api_token on ixn cookie', e);
          }
          // Extract life product options from API response
          const lifeProductOptions = {
            term_products: data.term_products || (data.product_types || []).filter(type => type.includes('Term') && !type.includes('ROP')),
            iul_products: (data.product_types || []).filter(type => type.includes('IUL')),
            guaranteed_life_products: data.guaranteed_life_products || [],
            return_of_premium_products: data.return_of_premium_products || [],
            other_products: data.other_products || [],
            product_limits: data.product_limits || 3
          };

          // Store life product options for use in form
          setLifeProductOptions(lifeProductOptions);

          // Load carriers and product features for life quoter
          const allowed_carrier_ids = data.carrier_ids || [];
          const carrierPromises = [];
          const productFeaturePromises = [];

          // Load carriers
          if (allowed_carrier_ids.length > 0) {
            carrierPromises.push(AlqApi.loadCarriers(allowed_carrier_ids, data.is_beta));
          }

          // Load product features
          productFeaturePromises.push(AlqApi.loadProductFeatures({ universal: true, id: lifeQuoter.quoter_token_id }));

          const guidesPromise = AlqApi.loadUnderwritingGuides(allowed_carrier_ids);

          Promise.all([...carrierPromises, ...productFeaturePromises, guidesPromise])
            .then(responses => {
              if (responses[0]) {
                setALQCarriers(responses[0]);
              }
              if (responses[1]) {
                setALQProductFeatures(responses[1]);
              }
              if (responses[2]) {
                setALQGuides(responses[2]);
              }

              setLoading(false);
              // Mark this tab as loaded
              setLoadedTabs(prev => new Set([...prev, tab]));
            })
            .catch(error => {
              console.error('Error loading life carriers/product features:', error);
              setLoading(false);
              // Mark this tab as loaded even if carriers/products fail
              setLoadedTabs(prev => new Set([...prev, tab]));
            });
        })
        .catch(error => {
          setLoading(false);
          console.error('Error loading life quoter:', error);
          if (error.response && error.response.data) {
            setError(error.response.data[0]);
          } else {
            setError({ type: -1, message: 'Please Provide the BGA-Quoter Token' });
          }
        });
    } else if (tab === 'new-ltc') {
      // LTC Quoter is not implemented yet, just mark as loaded
      setLoading(false);
      setLoadedTabs(prev => new Set([...prev, tab]));
    }
  };

  // Function to handle tab changes with Universal Quoter check
  const handleTabChange = useCallback(
    tab => {
      // Save current tab's form data before switching (only for unsaved quotes)
      if (!savedQuoteId && currentFormData) {
        if (activeTab === 'new-annuity') {
          setAnnuityTabFormData(currentFormData);
        } else if (activeTab === 'new-life') {
          setLifeTabFormData(currentFormData);
        }
      }

      setActiveTab(tab);
    },
    [activeTab, currentFormData, savedQuoteId]
  );

  // Optional: Function to force reload a specific tab's data
  const forceReloadTab = tab => {
    setLoadedTabs(prev => {
      const newSet = new Set(prev);
      newSet.delete(tab);
      return newSet;
    });
    loadQuoterData(tab);
  };

  // Function to initialize the Universal Quoter and load initial tab data
  const initializeQuoter = () => {
    loadUniversalQuoter()
      .then(uqDataFromApi => {
        // After Universal Quoter loads successfully, load the initial tab data
        // Pass the data directly to avoid state timing issues
        loadQuoterData(activeTab, uqDataFromApi);
      })
      .catch(error => {
        console.error('Failed to initialize Universal Quoter:', error);
        setError({ type: -1, message: 'Failed to initialize Universal Quoter' });
        // Error is already handled in loadUniversalQuoter
      });
  };

  useEffect(() => {
    // Initialize Universal Quoter first, then load initial tab data
    initializeQuoter();
  }, []); // Empty dependency array to run only once on mount

  useEffect(() => {
    if (uqData !== null && uqData !== undefined) {
      loadQuoterData(activeTab, uqData);
    }
  }, [activeTab]);

  // State for managing quoter configuration (similar to AgencyQuoter)
  const [quoterConfig, setQuoterConfig] = React.useState({
    agent: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      insurance_license_number: '',
      applicint_trusted_code: '',
      agency_code: '',
      agent_user_id: ''
    },
    proposed_insured: {
      annuity: {
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: 'Male',
        state: 'AL'
      },
      life: {
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: 'Male',
        state: 'AL'
      }
    },
    client: {
      first_name: '',
      last_name: ''
    },
    vive_enabled: false,
    quote_data: {
      current_age: 50,
      nearest_age: 0,
      tobacco: false,
      health_categories: null,
      premium: 150000,
      face_amounts: [250000],
      flat_extra: '0.00',
      product_types: null,
      carrier_ids: [],
      riders: [],
      child_rider_units: '0',
      premium_mode: 'Annual',
      adb_rider: false,
      wop_rider: false
    }
  });

  const [disableQuoter, setDisableQuoter] = React.useState(false);
  const [savedQuoteId, setSavedQuoteId] = React.useState(null);

  // Initialize quoterConfig from backend data on mount
  useEffect(() => {
    const backendData = window?.ixnDataModel?.IXNModel || window?.EditQuoteModel?.IXNQuote?.IXNModel || window?.ixn_data?.IXNModel;

    if (backendData && backendData.quote_data) {
      // Track if this is a saved quote
      setSavedQuoteId(backendData.id || null);

      setQuoterConfig(prev => ({
        ...prev,
        agent: backendData.agent || prev.agent,
        client: backendData.client || prev.client,
        proposed_insured: {
          annuity: {
            ...prev.proposed_insured.annuity,
            ...(backendData.proposed_insured?.annuity || {})
          },
          life: {
            ...prev.proposed_insured.life,
            ...(backendData.proposed_insured?.life || {})
          }
        },
        quote_data: {
          ...prev.quote_data,
          ...backendData.quote_data
        },
        vive_enabled: backendData.vive_enabled !== undefined ? backendData.vive_enabled : prev.vive_enabled
      }));
    }
  }, []);

  // Setup window functions once Universal Quoter is loaded
  useEffect(() => {
    if (isUniversalQuoterLoaded) {
      // Helper function to calculate age from date of birth
      const calculateAge = dateOfBirth => {
        if (!dateOfBirth) return 50; // Default age
        const dob = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        return age;
      };

      // Function to get current quoter configuration
      window.IXN.get_quoter_config = () => {
        // Use tab-specific fields
        const dateOfBirth = activeTab === 'new-annuity' ? currentFormData?.dateOfBirthAnnuity : currentFormData?.dateOfBirthLife;
        const gender = activeTab === 'new-annuity' ? currentFormData?.genderAnnuity : currentFormData?.genderLife;
        const jurisdiction = activeTab === 'new-annuity' ? currentFormData?.jurisdictionAnnuity : currentFormData?.jurisdictionLife;

        const currentAge = calculateAge(dateOfBirth);
        const nearestAge = currentAge + 1;

        // Helper function to get life products for the active product tab
        const getLifeProductsForActiveTab = () => {
          const selectedProducts = currentFormData?.selectedProductTypes || currentFormData?.productTypes || [];
          const activeProductTab = currentFormData?.activeProductTab || 'TERM';

          // Get products for the active product tab from lifeProductOptions
          let availableProductsForTab = [];
          switch (activeProductTab) {
            case 'TERM':
              availableProductsForTab = lifeProductOptions?.term_products || [];
              break;
            case 'GUL':
              availableProductsForTab = lifeProductOptions?.guaranteed_life_products || [];
              break;
            case 'IUL':
              availableProductsForTab = lifeProductOptions?.iul_products || [];
              break;
            case 'ROP':
              availableProductsForTab = lifeProductOptions?.return_of_premium_products || [];
              break;
            case 'OTHER':
              availableProductsForTab = lifeProductOptions?.other_products || [];
              break;
            default:
              availableProductsForTab = [];
          }

          // Filter selected products to only include those from the active tab
          return selectedProducts.filter(product => availableProductsForTab.includes(product));
        };

        // Unified quote data structure containing all fields for annuity, life, and LTC
        const quote_data = {
          // Common fields - now tab-specific
          current_age: currentAge,
          nearest_age: nearestAge,
          carrier_ids: (currentFormData?.selectedCarriers || []).map(carrier => (typeof carrier === 'object' && carrier?.id ? carrier.id : carrier)),
          health_categories: currentFormData.healthClass || ['Preferred Plus'],
          product_features: currentFormData.features || currentFormData.productFeatures || [],
          activeTab,

          // Annuity-specific fields
          product_types:
            activeTab === 'new-annuity'
              ? (currentFormData?.productTypes || []).map(pt => annuityProductTypeMap[pt] || pt)
              : currentFormData?.productTypes || currentFormData?.selectedProductTypes || [],
          product_types_annuity: activeTab === 'new-annuity' ? (currentFormData?.productTypes || []).map(pt => annuityProductTypeMap[pt] || pt) : [],
          product_types_life: activeTab === 'new-life' ? getLifeProductsForActiveTab() : [],
          premium: activeTab === 'new-annuity' ? parseFloat(currentFormData?.premiumAnnuity) || 150000 : parseFloat(currentFormData?.premiumLife) || 150000,
          am_best_rating: currentFormData?.amBestRating || ['Any'],
          bonus_rate: currentFormData?.bonusRate || false,
          bonus_exc: currentFormData?.bonusExc || false,
          gmb_rider: currentFormData?.gmbRider || false,
          index_id: currentFormData?.indexId || '',
          min_initial_rate: currentFormData?.minInitialRate || '0.0',
          guaranteed_period: currentFormData?.guaranteedPeriod || ['0'],
          surrender_charge_period: currentFormData?.surrenderChargePeriod || '',
          taxTreatment: currentFormData?.taxTreatment || 'Non-Qualified',
          gmb_elected: currentFormData?.gmbElected || false,
          quote_rider_income: currentFormData?.quoteRiderIncome || false,
          income_type: currentFormData?.incomeType || null,
          joint_covered_life_dob: currentFormData?.jointCoveredLifeDOB || null,

          // Life-specific fields
          product_limits: currentFormData?.productLimits || 3,
          enable_quote_by_premium: false,
          tobacco: currentFormData?.tobacco || false,
          face_amounts: currentFormData?.faceAmounts || [500000],
          flat_extra: currentFormData?.flatExtra || '0.00',
          // Build riders array from boolean flags
          riders: (() => {
            const ridersList = [];
            if (currentFormData?.adb_rider) ridersList.push('ADB');
            if (currentFormData?.wop_rider) ridersList.push('WOP');
            return ridersList;
          })(),
          child_rider_units: currentFormData?.child_rider_units || '',
          adb_rider: currentFormData?.adb_rider || false,
          wop_rider: currentFormData?.wop_rider || false,
          premium_mode: currentFormData?.premiumMode || 'Annual',
          activeProductTab: currentFormData?.activeProductTab || 'TERM',

          // IUL-specific fields
          death_benefit_option: currentFormData?.death_benefit_option || 'Level',
          annual_premium_iul: currentFormData?.annual_premium_iul || '',
          initial_premium_iul: currentFormData?.initial_premium_iul || '',
          to_age_iul: currentFormData?.to_age_iul || '',
          one_time_premium_iul: currentFormData?.one_time_premium_iul || '',
          face_amount_type_iul: currentFormData?.face_amount_type_iul || 'Minimum',
          specified_face_amount_iul: currentFormData?.specified_face_amount_iul || '',

          // Quote Value and Solve For options
          quote_value: currentFormData?.quoteValue || '5',
          solve_for_annuity: activeTab === 'new-annuity' ? currentFormData?.solveFor || 'Accumulation' : quoterConfig?.quote_data?.solve_for_annuity || 'Accumulation',
          solve_for_life: activeTab === 'new-life' ? currentFormData?.solveFor || 'Premium' : quoterConfig?.quote_data?.solve_for_life || 'Premium'
        };

        // Build agent object from currentFormData with quoterConfig as fallback
        const agent = {
          ...quoterConfig.agent,
          first_name: currentFormData?.firstName || quoterConfig?.agent?.first_name,
          last_name: currentFormData?.lastName || quoterConfig?.agent?.last_name,
          phone: currentFormData?.agentPhone || quoterConfig?.agent?.phone
        };

        // Build proposed_insured object - always use currentFormData if available, regardless of active tab
        // Note: The form uses generic fields (jurisdiction, gender, etc.) that apply to the active tab
        // We need to read the correct field based on activeTab and map to tab-specific proposed_insured structure
        const proposed_insured = {
          annuity: {
            first_name: currentFormData?.proposedInsuredAnnuityFirstName || quoterConfig?.proposed_insured?.annuity?.first_name || '',
            last_name: currentFormData?.proposedInsuredAnnuityLastName || quoterConfig?.proposed_insured?.annuity?.last_name || '',
            date_of_birth: currentFormData?.dateOfBirthAnnuity || quoterConfig?.proposed_insured?.annuity?.date_of_birth || '',
            gender: currentFormData?.genderAnnuity || quoterConfig?.proposed_insured?.annuity?.gender || 'Male',
            state: currentFormData?.jurisdictionAnnuity || quoterConfig?.proposed_insured?.annuity?.state || 'AL'
          },
          life: {
            first_name: currentFormData?.proposedInsuredLifeFirstName || quoterConfig?.proposed_insured?.life?.first_name || '',
            last_name: currentFormData?.proposedInsuredLifeLastName || quoterConfig?.proposed_insured?.life?.last_name || '',
            date_of_birth: currentFormData?.dateOfBirthLife || quoterConfig?.proposed_insured?.life?.date_of_birth || '',
            gender: currentFormData?.genderLife || quoterConfig?.proposed_insured?.life?.gender || 'Male',
            state: currentFormData?.jurisdictionLife || quoterConfig?.proposed_insured?.life?.state || 'AL'
          }
        };

        return {
          agent,
          client: quoterConfig.client,
          proposed_insured,
          quote_data
        };
      };

      // Function to update quoter configuration
      window.IXN.update_quoter_config = data => {
        setQuoterConfig(prevConfig => {
          const newConfig = { ...prevConfig };

          //update proposed insured details if provided - handle nested structure
          if (data.proposed_insured) {
            // Handle nested structure
            if (data.proposed_insured.annuity) {
              // Only update fields with non-empty values to avoid overwriting with empty strings
              const annuityUpdates = {};
              Object.keys(data.proposed_insured.annuity).forEach(key => {
                if (data.proposed_insured.annuity[key] !== '') {
                  annuityUpdates[key] = data.proposed_insured.annuity[key];
                }
              });
              newConfig.proposed_insured.annuity = {
                ...newConfig.proposed_insured.annuity,
                ...annuityUpdates
              };
            }
            if (data.proposed_insured.life) {
              // Only update fields with non-empty values to avoid overwriting with empty strings
              const lifeUpdates = {};
              Object.keys(data.proposed_insured.life).forEach(key => {
                if (data.proposed_insured.life[key] !== '') {
                  lifeUpdates[key] = data.proposed_insured.life[key];
                }
              });
              newConfig.proposed_insured.life = {
                ...newConfig.proposed_insured.life,
                ...lifeUpdates
              };
            }
          }

          // Update agent information
          if (data.agent) {
            newConfig.agent = {
              ...newConfig.agent,
              ...data.agent
            };
          }

          // Update client information
          if (data.client) {
            newConfig.client = {
              ...newConfig.client,
              ...data.client
            };
          }

          // Update vive_enabled
          if (typeof data.vive_enabled !== 'undefined') {
            newConfig.vive_enabled = data.vive_enabled;
          }

          // Update quote_data
          if (data.quote_data) {
            // Invert annuityProductTypeMap for reverse lookup
            const annuityProductTypeReverseMap = Object.entries(annuityProductTypeMap).reduce((acc, [abbr, full]) => {
              acc[full] = abbr;
              return acc;
            }, {});

            let updatedProductTypes = [];
            if (activeTab === 'new-annuity' && Array.isArray(data.quote_data.product_types)) {
              updatedProductTypes = data.quote_data.product_types.map(pt => annuityProductTypeReverseMap[pt] || pt);
            }

            newConfig.quote_data = {
              ...newConfig.quote_data,
              ...data.quote_data,
              product_types: activeTab === 'new-annuity' ? updatedProductTypes : [],
              premium: parseFloat(data.quote_data.premium) || newConfig.quote_data.premium,
              face_amounts: data.quote_data.face_amounts && data.quote_data.face_amounts.length > 0 ? data.quote_data.face_amounts : newConfig.quote_data.face_amounts
            };
          }

          return newConfig;
        });

        // RTB-242: Sync currentFormData so get_quoter_config returns the saved values
        // Without this, user changes in the widget update currentFormData, but saved values
        // loaded from the backend only update quoterConfig, creating a mismatch
        if (data.quote_data) {
          setCurrentFormData(prevData => {
            const updatedData = { ...prevData };

            // Sync premium value
            if (data.quote_data.premium !== undefined) {
              if (activeTab === 'new-annuity') {
                updatedData.premiumAnnuity = data.quote_data.premium;
              } else if (activeTab === 'new-life') {
                updatedData.premiumLife = data.quote_data.premium;
              }
              updatedData.premium = data.quote_data.premium;
            }

            // Sync tax treatment
            if (data.quote_data.taxTreatment !== undefined && data.quote_data.taxTreatment !== null) {
              updatedData.taxTreatment = data.quote_data.taxTreatment;
            }

            if (data.quote_data.activeProductTab !== undefined && data.quote_data.activeProductTab !== null) {
              updatedData.activeProductTab = data.quote_data.activeProductTab;
            }

            // Sync quote_value
            if (data.quote_data.quote_value !== undefined && data.quote_data.quote_value !== null) {
              updatedData.quoteValue = data.quote_data.quote_value;
            }

            // Sync solve_for based on active tab
            if (activeTab === 'new-annuity' && data.quote_data.solve_for_annuity !== undefined && data.quote_data.solve_for_annuity !== null) {
              updatedData.solveFor = data.quote_data.solve_for_annuity;
            } else if (activeTab === 'new-life' && data.quote_data.solve_for_life !== undefined && data.quote_data.solve_for_life !== null) {
              updatedData.solveFor = data.quote_data.solve_for_life;
            }

            // Sync agent information
            if (data.agent) {
              updatedData.firstName = data.agent.first_name || updatedData.firstName;
              updatedData.lastName = data.agent.last_name || updatedData.lastName;
              updatedData.agentEmail = data.agent.email || updatedData.agentEmail;
              updatedData.agentPhone = data.agent.phone || updatedData.agentPhone;
              updatedData.agentLicenseNumber = data.agent.insurance_license_number || updatedData.agentLicenseNumber;
            }

            // Sync proposed insured information (separate from agent) - handle nested structure
            if (data.proposed_insured) {
              // Sync both annuity and life fields regardless of active tab to ensure proper fallbacks
              if (data.proposed_insured.annuity) {
                updatedData.proposedInsuredAnnuityFirstName = data.proposed_insured.annuity.first_name || updatedData.proposedInsuredAnnuityFirstName;
                updatedData.proposedInsuredAnnuityLastName = data.proposed_insured.annuity.last_name || updatedData.proposedInsuredAnnuityLastName;
                updatedData.dateOfBirthAnnuity = data.proposed_insured.annuity.date_of_birth || updatedData.dateOfBirthAnnuity;
                updatedData.genderAnnuity = data.proposed_insured.annuity.gender || updatedData.genderAnnuity;
                updatedData.jurisdictionAnnuity = data.proposed_insured.annuity.state || updatedData.jurisdictionAnnuity;
              }
              if (data.proposed_insured.life) {
                updatedData.proposedInsuredLifeFirstName = data.proposed_insured.life.first_name || updatedData.proposedInsuredLifeFirstName;
                updatedData.proposedInsuredLifeLastName = data.proposed_insured.life.last_name || updatedData.proposedInsuredLifeLastName;
                updatedData.dateOfBirthLife = data.proposed_insured.life.date_of_birth || updatedData.dateOfBirthLife;
                updatedData.genderLife = data.proposed_insured.life.gender || updatedData.genderLife;
                updatedData.jurisdictionLife = data.proposed_insured.life.state || updatedData.jurisdictionLife;
              }
            }

            // Sync face amounts for life insurance quotes
            if (data.quote_data.face_amounts && data.quote_data.face_amounts.length > 0) {
              updatedData.faceAmounts = data.quote_data.face_amounts;
              updatedData.faceAmount = data.quote_data.face_amounts[0];
            }

            // Sync premium mode for life insurance quotes (no activeTab check - this is life-specific data)
            if (data.quote_data.premium_mode !== undefined && data.quote_data.premium_mode !== null) {
              updatedData.premiumMode = data.quote_data.premium_mode;
            }

            // Sync riders for life insurance quotes
            if (data.quote_data.adb_rider !== undefined) {
              updatedData.adb_rider = data.quote_data.adb_rider;
            }
            if (data.quote_data.wop_rider !== undefined) {
              updatedData.wop_rider = data.quote_data.wop_rider;
            }

            // Sync child rider units
            if (data.quote_data.child_rider_units !== undefined) {
              updatedData.child_rider_units = data.quote_data.child_rider_units;
            }

            // Sync flat extra
            if (data.quote_data.flat_extra !== undefined) {
              updatedData.flatExtra = data.quote_data.flat_extra;
            }

            // Sync health categories (table ratings)
            if (data.quote_data.health_categories && Array.isArray(data.quote_data.health_categories)) {
              updatedData.healthClass = data.quote_data.health_categories;

              // If any of the health categories are table ratings (Table A-V), set showTableRatings to true
              const TABLE_RATINGS = Array.from({ length: 22 }, (_, i) => `Table ${String.fromCharCode(65 + i)}`);
              const hasTableRatings = data.quote_data.health_categories.some(hc => TABLE_RATINGS.includes(hc));
              if (hasTableRatings) {
                updatedData.showTableRatings = true;
              }
            }

            // Sync product types from the tab-specific fields
            if (data.quote_data.product_types_life && data.quote_data.product_types_life.length > 0) {
              updatedData.productTypes = data.quote_data.product_types_life;
              updatedData.selectedProductTypes = data.quote_data.product_types_life;
            } else if (data.quote_data.product_types_annuity && data.quote_data.product_types_annuity.length > 0) {
              // Convert full names back to abbreviations for annuity products
              const reverseMap = Object.entries(annuityProductTypeMap).reduce((acc, [abbr, full]) => {
                acc[full] = abbr;
                return acc;
              }, {});
              const abbreviatedTypes = data.quote_data.product_types_annuity.map(pt => reverseMap[pt] || pt);
              updatedData.productTypes = abbreviatedTypes;
            }

            return updatedData;
          });
        }

        // If active tab change is requested
        if (data.quote_data.activeTab && data.quote_data.activeTab !== activeTab) {
          handleTabChange(data.quote_data.activeTab);
        }
      };

      // Function to disable the quoter
      window.IXN.disableQuoter = () => {
        setDisableQuoter(true);
      };

      // Function to enable the quoter
      window.IXN.enableQuoter = () => {
        setDisableQuoter(false);
      };

      // Also provide backwards compatibility with generic names
      // if (!window.IXN.get_quoter_config) {
      //   window.IXN.get_quoter_config = window.IXN.get_universal_quoter_config;
      // }
      // if (!window.IXN.update_quoter_config) {
      //   window.IXN.update_quoter_config = window.IXN.update_universal_quoter_config;
      // }
      // if (!window.IXN.disableQuoter) {
      //   window.IXN.disableQuoter = window.IXN.disable_universal_quoter;
      // }
      // if (!window.IXN.enableQuoter) {
      //   window.IXN.enableQuoter = window.IXN.enable_universal_quoter;
      // }
    }

    // Cleanup function to remove window functions when component unmounts
    return () => {
      if (window.IXN) {
        delete window.IXN.get_universal_quoter_config;
        delete window.IXN.update_universal_quoter_config;
        delete window.IXN.disable_universal_quoter;
        delete window.IXN.enable_universal_quoter;
      }
    };
  }, [isUniversalQuoterLoaded, quoterConfig, activeTab, aaqQuoter, alqQuoter, ltcQuoter, currentFormData]);
  return (
    <UniversalQuoterComponent
      aaqCarriers={aaqCarriers}
      aaqCriteriaOptions={aaqCriteriaOptions}
      aaqProducts={aaqProducts}
      aaqProductsFeatures={aaqProductsFeatures}
      aaqQuoter={aaqQuoter}
      activeTab={activeTab}
      alqCarriers={alqCarriers}
      alqGuides={alqGuides}
      alqProductFeatures={alqProductFeatures}
      alqQuoter={alqQuoter}
      // Annuity data
      disableQuoter={disableQuoter}
      error={error}
      isUniversalQuoterLoaded={isUniversalQuoterLoaded}
      lifeProductOptions={lifeProductOptions}
      // Life data
      loading={loading}
      ltcQuoter={ltcQuoter}
      onFormDataChange={setCurrentFormData}
      onQuoterConfigChange={setQuoterConfig}
      // Quoter configuration
      onTabChange={handleTabChange}
      quoterConfig={quoterConfig}
      savedQuoteId={savedQuoteId}
      solveType={solveType}
      // Tab-specific form data for unsaved quotes
      annuityTabFormData={annuityTabFormData}
      lifeTabFormData={lifeTabFormData}
      // Form data handler
      uqData={uqData}
    />
  );
};

const container = document.getElementById('universal-quoter-component');
const root = createRoot(container);
root.render(
  <PostHogProvider client={posthog}>
    <UniversalQuoter />
  </PostHogProvider>
);
